import { useFormContext, useWatch } from "react-hook-form";
import { FormCampoTipo } from "@workflow/types";

import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FormDatePicker } from "@/components/campos-formulario/novos/FormDatePicker";
import { FormCampoDTO, FormSchemaDTO } from "@/types/conclusao.type";
import { condicaoSatisfeita } from "./conclusao-form-utils";
import { CampoArquivoUpload } from "./CampoArquivoUpload";

interface FormRendererProps {
  schema: FormSchemaDTO;
}

/** Renderiza um FormSchema declarativo como campos controlados (react-hook-form). */
export function FormRenderer({ schema }: Readonly<FormRendererProps>) {
  return (
    <div className="flex flex-col gap-4">
      {schema.campos.map((campo) => (
        <CampoField key={campo.nome} campo={campo} />
      ))}
    </div>
  );
}

function CampoField({ campo }: Readonly<{ campo: FormCampoDTO }>) {
  const { control } = useFormContext();

  // exibirSe: observa o campo de referência e oculta quando a condição não bate.
  const watched = useWatch({ control, name: campo.exibirSe?.campo ?? "__none__" });
  if (campo.exibirSe && !condicaoSatisfeita(campo.exibirSe, watched)) return null;

  const isArquivo = campo.tipo === FormCampoTipo.ARQUIVO;

  return (
    <FormField
      control={control}
      name={campo.nome}
      render={({ field }) => (
        <FormItem className="space-y-2">
          <FormLabel className="text-sm font-semibold">
            {campo.label}
            {campo.obrigatorio && <span className="ml-1 text-destructive">*</span>}
          </FormLabel>

          <FormControl>{renderControl(campo, field)}</FormControl>

          {!isArquivo && campo.ajuda && (
            <p className="text-xs text-muted-foreground">{campo.ajuda}</p>
          )}
          <FormMessage className="text-xs" />
        </FormItem>
      )}
    />
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function renderControl(campo: FormCampoDTO, field: any) {
  switch (campo.tipo) {
    case FormCampoTipo.TEXTO_LONGO:
      return (
        <Textarea placeholder={campo.ajuda} className="min-h-[100px] resize-none" {...field} />
      );

    case FormCampoTipo.NUMERO:
      return (
        <Input
          type="number"
          placeholder={campo.ajuda}
          value={field.value ?? ""}
          onBlur={field.onBlur}
          onChange={(e) =>
            field.onChange(e.target.value === "" ? undefined : Number(e.target.value))
          }
        />
      );

    case FormCampoTipo.BOOLEANO:
      return (
        <div className="flex items-center gap-2">
          <Checkbox checked={!!field.value} onCheckedChange={(v) => field.onChange(!!v)} />
          <span className="text-sm text-muted-foreground">{campo.ajuda ?? "Sim"}</span>
        </div>
      );

    case FormCampoTipo.DATA:
      return <FormDatePicker value={field.value} onChange={field.onChange} onBlur={field.onBlur} />;

    case FormCampoTipo.SELECT:
      return (
        <Select value={field.value ?? ""} onValueChange={field.onChange}>
          <SelectTrigger>
            <SelectValue placeholder={campo.ajuda ?? "Selecione..."} />
          </SelectTrigger>
          <SelectContent>
            {(campo.opcoes ?? []).map((opcao) => (
              <SelectItem key={opcao.valor} value={opcao.valor}>
                {opcao.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );

    case FormCampoTipo.ARQUIVO:
      return (
        <CampoArquivoUpload
          campo={campo}
          file={field.value ?? null}
          onFileChange={field.onChange}
        />
      );

    case FormCampoTipo.REFERENCIA:
      return (
        <Input
          type="number"
          placeholder={campo.ajuda ?? "ID do registro"}
          value={field.value ?? ""}
          onBlur={field.onBlur}
          onChange={(e) =>
            field.onChange(e.target.value === "" ? undefined : Number(e.target.value))
          }
        />
      );

    case FormCampoTipo.TEXTO:
    default:
      return <Input placeholder={campo.ajuda} {...field} />;
  }
}
